import {
  makeVar,
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery,
  useReactiveVar,
} from '@apollo/client';
import {
  Checkbox,
  DefaultButton,
  Dropdown,
  IDetailsRowProps,
  IRenderFunction,
  PrimaryButton,
  Stack,
  Text,
  useTheme,
} from '@fluentui/react';
import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { FilterArrayType } from 'common/components/Filters';
import { InfiniteList } from 'common/components/InfiniteList';
import {
  MultiSelectOption,
  MultiSelectTags,
} from 'common/components/MultiSelectTags';
import { RequestMessageModal } from 'common/components/RequestMessageModal';
import { ColumnData } from 'common/components/SearchBar';
import { TABLE_ROWS } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import { EntityDeleteInput } from 'common/types/globalTypes';
import { EntityAction, EntityType } from 'common/types/utility';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import Decimal from 'decimal.js';
import {
  PurchaseOrderApprovalCreate,
  PurchaseOrderApprovalCreateVariables,
} from 'purchaseOrder/__generated__/PurchaseOrderApprovalCreate';
import {
  PurchaseOrderDelete,
  PurchaseOrderDeleteVariables,
} from 'purchaseOrder/__generated__/PurchaseOrderDelete';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import { StringParam } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';
import { expandedNav, globalMode, setGlobalSearchText } from 'utility/cache/ui';
import PURCHASE_ORDER_APPROVAL_CREATE from '../PurchaseOrderApprovalCreate.graphql';
import PURCHASE_ORDER_DELETE from '../PurchaseOrderDelete.graphql';
import { ExportPurchaseOrders } from './ExportPurchaseOrders';
import { POSearchBar } from './POSearchBar';
import PURCHASE_ORDER_SEARCH from './PurchaseOrderSearch.graphql';
import PURCHASE_ORDER_FILTER_TOTAL from './PurchaseOrderSearchFilterTotals.graphql';
import PURCHASE_ORDER_SEARCH_HINTS from './PurchaseOrderSearchHints.graphql';
import { StampTransactionFooter } from './StampTransaction';
import {
  PurchaseOrderSearch,
  PurchaseOrderSearchVariables,
} from './__generated__/PurchaseOrderSearch';
import {
  PurchaseOrderSearchFilterTotals,
  PurchaseOrderSearchFilterTotalsVariables,
} from './__generated__/PurchaseOrderSearchFilterTotals';
import {
  PurchaseOrderSearchHints,
  PurchaseOrderSearchHintsVariables,
} from './__generated__/PurchaseOrderSearchHints';
import { useColumns } from './column.data';
import { useStyles } from './index.styles';
import {
  buildPOSearchPayloads,
  checkBoxStyle,
  CommonRowRenderer,
  createFooterAmountRenderer,
  dropdownStyles,
  handleApprovalRequest,
  PurchaseOrderItem,
  renderItemColumn,
  transformPOListData,
} from './poListComponents';
import { usePurchaseOrderStampPrivileges } from './poListComponents/hooks/usePurchaseOrderStampPrivileges';
import {
  getFilterParams,
  listTypeEnum,
  listTypeOptions,
  toOrderByVariable,
} from './utils';

import { HelpIcon } from 'common/trainingSystem/HelpIcon';

export const setPOListViewOptions = makeVar<{
  isSigning: boolean;
  isHistory: boolean;
  isSearchAll: boolean;
}>({ isSigning: true, isHistory: false, isSearchAll: true });

export const PurchaseOrderList = () => {
  const theme = useTheme();
  const POListViewOptions = useReactiveVar(setPOListViewOptions);
  const commonStyles = useCommonStyles();
  const styles = useStyles();
  const history = useHistory();
  const [urlParameters, setUrlParameters] = useQueryParams({
    t: StringParam,
  });
  const { t: searchParam } = urlParameters;
  const globalSearchText = useReactiveVar(setGlobalSearchText);
  const globalState = useReactiveVar(globalMode);
  const { columns } = useColumns();

  const [gridColumns, setGridColumns] = useState<ColumnData[]>(columns);
  const [selectedRows, setSelectedRows] = useState<PurchaseOrderItem[]>([]);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [searchTextValue, setSearchTextValue] = useState<string | null>(
    searchParam! || null
  );
  const [searchFilters, setSearchFilters] = useState<FilterArrayType[]>([]);
  const [searchHints, setSearchHints] = useState<number | null>();
  const [hintsVisibility, setHintsVisibility] = useState<boolean>(false);
  const [isAllRequestChecked, setIsAllRequestChecked] = useState(false);
  const [requestedIndices, setRequestedIndices] = useState<number[]>([]);

  const SearchFiltersUpdated = searchFilters.filter(
    (f) =>
      f.filterKey !== 'filterSupplierId' &&
      f.filterKey !== 'filterMultiStatusTypes'
  );
  const filterParams = getFilterParams(searchFilters);
  const filterSupplierId = searchFilters
    .find((f) => f.filterKey === 'filterSupplierId')
    ?.value?.toString();
  const filterMultiStatusTypes = searchFilters.find(
    (f) => f.filterKey === 'filterMultiStatusTypes'
  )?.value as number[];

  // STAMP PURCHASE ORDER
  const [stampTransaction, setStampTransaction] = useState(false);
  const [transactionStampField, setTransactionStampField] = useState<
    Map<string, string>
  >(new Map());

  const renderRef = useRef(false);

  const [
    purchaseOrderApprovalCreate,
    { loading: purchaseOrderApprovalCreateLoading },
  ] = useMutation<
    PurchaseOrderApprovalCreate,
    PurchaseOrderApprovalCreateVariables
  >(PURCHASE_ORDER_APPROVAL_CREATE, { errorPolicy: 'all' });

  const [deletePurchaseOrder] = useMutation<
    PurchaseOrderDelete,
    PurchaseOrderDeleteVariables
  >(PURCHASE_ORDER_DELETE, {
    errorPolicy: 'all',
  });

  const {
    loading,
    data,
    error: PoSearcherror,
    networkStatus,
    refetch,
    fetchMore,
    variables: purchaseOrderSearchVariables,
  } = useQuery<PurchaseOrderSearch, PurchaseOrderSearchVariables>(
    PURCHASE_ORDER_SEARCH,
    {
      variables: {
        first: TABLE_ROWS,
        orderBy: ['PURCHASE_ORDER_NUMBER_DESC', 'PRIMARY_KEY_ASC'],
        isHistorySearch: false,
        isSigningSearch: true,
        isSearchAllData: true,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );
  const { data: purchaseOrderFilterTotal, refetch: refetchFilterTotal } =
    useQuery<
      PurchaseOrderSearchFilterTotals,
      PurchaseOrderSearchFilterTotalsVariables
    >(PURCHASE_ORDER_FILTER_TOTAL, {
      variables: {
        isSigningSearch: true,
        isHistorySearch: false,
        isLinkSearch: false,
        isSearchAllData: true,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    });

  const [fetchHints, { data: hintsData }] = useLazyQuery<
    PurchaseOrderSearchHints,
    PurchaseOrderSearchHintsVariables
  >(PURCHASE_ORDER_SEARCH_HINTS, {
    variables: {
      filterSupplierId,
      searchRequest: {
        searchText: [searchTextValue ? searchTextValue : ''],
        searchHintId: searchHints ? searchHints : null,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const handleSearchGlobal = (
    showMore: boolean,
    newHints: boolean,
    globalValue: boolean
  ) => {
    if (!showMore) setSelectedRows([]);
    setIsAllRequestChecked(false);

    const { variables, totalFilterVariables, variableHints, searchRequest } =
      buildPOSearchPayloads({
        showMore,
        newHints,
        globalValue,
        data,
        searchTextValue,
        searchHints,
        sortOrderParam,
        SearchFiltersUpdated,
        filterMultiStatusTypes,
        POListViewOptions,
        filterParams,
        supplierId: filterSupplierId,
      });

    if (showMore) {
      fetchMore?.({ variables });
    } else {
      refetch(variables);
      refetchFilterTotal(totalFilterVariables);
    }

    if (newHints && searchRequest && !searchHints) {
      setSearchHints(null);
      fetchHints({ variables: variableHints });
    }
  };
  const _onRenderRow: IRenderFunction<IDetailsRowProps> = (
    props,
    defaultRender
  ) => (
    <CommonRowRenderer<PurchaseOrderItem>
      props={props}
      defaultRender={defaultRender}
      isUrgent={!Boolean((props?.item as PurchaseOrderItem)._urgencyLevel)}
    />
  );
  const handleSearch = (showMore: boolean, newHints: boolean) =>
    handleSearchGlobal(showMore, newHints, globalState);

  const handleSearchMemo = useCallback(handleSearch, [
    searchFilters,
    sortOrderParam,
    searchTextValue,
    searchHints,
    POListViewOptions,
  ]);

  useEffect(() => {
    if (renderRef.current) handleSearchMemo(false, true);
    else renderRef.current = true;
  }, [
    searchFilters,
    sortOrderParam,
    searchTextValue,
    searchHints,
    handleSearchMemo,
    POListViewOptions,
  ]);

  const searchHintsOptions: MultiSelectOption[] =
    hintsData?.purchaseOrderSearchHints?.nodes.map(
      (hint) =>
        ({
          key: hint.id?.toString(),
          text: `${hint.caption} (${hint.resultsCount})`,
        } as MultiSelectOption)
    ) || [];

  const HINTS_AVAILABLE =
    searchHintsOptions.length > 0 && searchTextValue?.length! > 0;

  const refetching = loading && networkStatus !== NetworkStatus.fetchMore;

  const transformedData = useMemo(
    () =>
      refetching ? [] : transformPOListData(data?.purchaseOrderSearch?.nodes),
    [data?.purchaseOrderSearch?.nodes, refetching]
  );

  const hasNextPage = data?.purchaseOrderSearch?.pageInfo.hasNextPage;

  const { totalAmount1, totalAmount2, totalAmount3, totalCount1 } = {
    ...purchaseOrderFilterTotal?.purchaseOrderSearchFilterTotals,
  };
  const totalsMap = {
    controlTotalAmount: totalAmount1,
    _baseCurrencyAmount: totalAmount2,
    _spotCurrencyAmount: totalAmount3,
  };

  const _renderDetailsFooterItemColumn = createFooterAmountRenderer({
    totalsMap,
    className: styles.footerTotal,
  });

  const showFooter =
    !refetching &&
    parseFloat(totalAmount1 || '0.0') > 0 &&
    (totalCount1 || 0) > 0;

  const _onColumnClick = useCallback(
    async (_ev?: React.MouseEvent<HTMLElement>, clickedColumn?: ColumnData) => {
      if (clickedColumn) {
        const { newColumns, desc } = getSortedColumns(
          clickedColumn,
          gridColumns
        );
        setGridColumns(newColumns);
        setSortOrderParam({
          column: clickedColumn.key,
          direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
        });
        await refetch({
          after: null,
          orderBy: toOrderByVariable({
            column: clickedColumn.key,
            direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
          }),
        });
      }
    },
    [gridColumns, refetch]
  );

  const _onConfirm = async () => {
    const selectedData: PurchaseOrderItem[] = selectedRows;
    const deletedRows: PurchaseOrderItem[] = selectedData.filter(
      (item) => item._isDeletable
    );
    const entityDelete: EntityDeleteInput[] = selectedData
      .filter((item) => item._isDeletable)
      .map((item) => {
        return { id: item.id, rowTimestamp: item._rowTimestamp! };
      });
    const { errors } = await deletePurchaseOrder({
      variables: {
        input: {
          entityDelete,
        },
      },
      update(cache) {
        deletedRows.forEach((item) => {
          const identity = cache.identify({
            ...item,
          });
          //  DELETE CACHE REQUIRES FIX - TEMPORARY FIX MANUAL CACHE UPDATES
          cache.evict({ id: identity });
          cache.gc();
          cache.modify({
            fields: {
              purchaseOrderSearch(existing, { readField }) {
                if (!existing) return;

                const existingNodes = readField<any[]>('nodes', existing) ?? [];
                const newList = existingNodes.filter((ix) => {
                  const id = readField<string>('id', ix);
                  return id !== item.id;
                });

                return {
                  ...existing,
                  nodes: newList,
                };
              },

              purchaseOrderSearchFilterTotals(existing, { readField }) {
                if (!existing) return;

                const totalCount1 =
                  readField<number>('totalCount1', existing) ?? 0;
                const totalAmount1 =
                  readField<string>('totalAmount1', existing) ?? '0';
                const totalAmount2 =
                  readField<string>('totalAmount2', existing) ?? '0';
                const totalAmount3 =
                  readField<string>('totalAmount3', existing) ?? '0';

                if (totalCount1 !== 0) {
                  return {
                    ...existing,
                    totalCount1: totalCount1 - 1,
                    totalAmount1: new Decimal(totalAmount1)
                      .minus(item?.controlTotalAmount || '0')
                      .toNumber(),
                    totalAmount2: new Decimal(totalAmount2)
                      .minus(item?._baseCurrencyAmount || '0')
                      .toNumber(),
                    totalAmount3: new Decimal(totalAmount3)
                      .minus(item?._spotCurrencyAmount || '0')
                      .toNumber(),
                  };
                } else {
                  return {
                    ...existing,
                  };
                }
              },
            },
          });
        });
      },
    });

    if (errors?.length)
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    else {
      showCustomToast({
        content: 'Record Deleted Successfully',
        appearance: 'success',
      });
    }
  };

  useEffect(() => {
    return () => {
      setPOListViewOptions({
        ...POListViewOptions,
        isSigning: true,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showRequestAll = transformedData?.some(
    (invoice) => invoice._isStagedApprovalRequest
  );

  useEffect(() => {
    if (requestedIndices.length > 0) {
      const stagedRowsLength = selectedRows.filter(
        (item) => item._isStagedApprovalRequest
      ).length;
      if (stagedRowsLength !== requestedIndices.length && isAllRequestChecked) {
        setIsAllRequestChecked(false);
      } else {
        if (requestedIndices.length === selectedRows.length) {
          setIsAllRequestChecked(true);
        } else {
          if (isAllRequestChecked) setIsAllRequestChecked(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  useEffect(() => {
    if (POListViewOptions.isHistory) {
      const newColumns = gridColumns.map((item) => {
        if (item.key === 'transactionStamp')
          return { ...item, isVisible: false };
        else return item;
      });
      setGridColumns(newColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [POListViewOptions.isHistory]);

  const { stampingAllowed, stampButtonVisible } =
    usePurchaseOrderStampPrivileges(POListViewOptions);

  return (
    <>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
      >
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          className={commonStyles.listTitleContainer}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
            <Text variant="xLarge">Purchase Orders</Text>
            <HelpIcon appLinkType="PURCHASE_ORDER" />
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <Dropdown
              placeholder="Select"
              selectedKey={
                POListViewOptions.isSearchAll
                  ? listTypeEnum.all
                  : listTypeEnum.my
              }
              options={listTypeOptions}
              onChange={(event, option) => {
                if (event) {
                  const isSearchAll = option?.key === listTypeEnum.all;
                  setPOListViewOptions({ ...POListViewOptions, isSearchAll });
                }
              }}
              styles={dropdownStyles(theme)}
            />

            <ExportPurchaseOrders
              selectedRows={selectedRows}
              isSigningSearch={POListViewOptions.isSigning}
              isHistorySearch={POListViewOptions.isHistory}
              isSearchAllData={POListViewOptions.isSearchAll}
              isLinkSearch={globalState}
              searchFilters={searchFilters}
              filterSupplierId={filterSupplierId}
              purchaseOrderDocumentSearchRequest={
                searchTextValue ? { searchText: [searchTextValue] } : null
              }
              isSelectedAll={
                !!transformedData?.length &&
                selectedRows.length === transformedData.length
              }
            />
            {stampButtonVisible && (
              <DefaultButton
                text="Stamp"
                onClick={() => {
                  setStampTransaction((prevState) => !prevState);
                  expandedNav(stampTransaction);
                  const newColumns = gridColumns.map((item) => {
                    if (item.key === 'transactionStamp')
                      return { ...item, isVisible: !stampTransaction };
                    else return item;
                  });
                  setGridColumns(newColumns);
                }}
                iconProps={{
                  iconName: 'StampSmall',
                  styles: {
                    root: {
                      fill: theme.palette.black,
                    },
                  },
                }}
              />
            )}

            {showRequestAll && (
              <RequestMessageModal
                entityType={EntityType.PurchaseOrder}
                action={EntityAction.Request}
                disabled={
                  !selectedRows.some(
                    (selected) => selected._isStagedApprovalRequest
                  )
                }
                visible={selectedRows.length > 0}
                multiple={{
                  validCount: selectedRows.filter(
                    (selected) => selected._isStagedApprovalRequest
                  ).length,
                  invalidNames: selectedRows
                    .filter((selected) => !selected._isStagedApprovalRequest!)
                    .map((cannotDelete) => cannotDelete.purchaseOrderNumber!),
                }}
                buttonProps={{ text: 'Request Approval' }}
                onConfirm={async (
                  requestComment: string | null,
                  requiredDate: string | null
                ) => {
                  await handleApprovalRequest(
                    requestComment,
                    requiredDate,
                    selectedRows,
                    purchaseOrderApprovalCreate,
                    purchaseOrderSearchVariables,
                    setIsAllRequestChecked,
                    setRequestedIndices
                  );
                }}
                isLoading={purchaseOrderApprovalCreateLoading}
              />
            )}

            <ActionMessageModal
              entityType={EntityType.PurchaseOrder}
              disabled={!selectedRows.some((selected) => selected._isDeletable)}
              visible={selectedRows.length > 0}
              multiple={{
                validCount: selectedRows.filter(
                  (selected) => selected._isDeletable
                ).length,
                invalidNames: selectedRows
                  .filter((selected) => !selected._isDeletable)
                  .map((selected) => selected.purchaseOrderNumber || ''),
              }}
              onConfirm={_onConfirm}
            />
            <PrimaryButton
              onClick={() => history.push('/purchase-orders/purchase-order')}
              iconProps={{
                iconName: 'Add',
              }}
              text="New Purchase Order"
            />
          </Stack>
        </Stack>
        <POSearchBar
          urlParams={{
            searchParam: searchParam!,
          }}
          hintsAvailable={HINTS_AVAILABLE}
          columns={gridColumns}
          onToggleVisibility={(columns) => {
            setGridColumns(columns);
          }}
          onRefresh={() => {
            handleSearch(false, false);
          }}
          onFilterChange={(filters) => {
            setSearchFilters(filters);
          }}
          onEnterPress={(value, globalSearch) => {
            setSearchHints(null);
            setHintsVisibility(false);
            setSearchTextValue(value);
            setGlobalSearchText(value);
          }}
          onHintsViewToggle={() =>
            setHintsVisibility((prevState) => !prevState)
          }
          onGlobalToggle={(value) => {
            setPOListViewOptions({
              ...POListViewOptions,
              isHistory: false,
              isSigning: true,
            });
            handleSearchGlobal(false, true, value);
          }}
          isSigning={POListViewOptions.isSigning}
        />
        {hintsVisibility && HINTS_AVAILABLE && (
          <MultiSelectTags
            multiSelect={false}
            options={searchHintsOptions}
            onSelectionChange={(selctedTags) => {
              const hints = selctedTags.map((item) => parseInt(item.key));
              setSearchHints(hints[0]);
            }}
          />
        )}
        <Stack horizontal className={styles.checkboxContainer}>
          {showRequestAll && (
            <Checkbox
              boxSide="end"
              styles={checkBoxStyle}
              checked={isAllRequestChecked}
              label="Request All"
              onChange={(_, value) => {
                setIsAllRequestChecked(!isAllRequestChecked!);
                if (value) {
                  const requestedIndicesArray = transformedData?.reduce(
                    function (array: number[], element, index) {
                      if (element._isStagedApprovalRequest) array.push(index);
                      return array;
                    },
                    []
                  );
                  setRequestedIndices(requestedIndicesArray || []);
                } else {
                  setRequestedIndices([]);
                }
              }}
            />
          )}
        </Stack>
      </Stack>
      <InfiniteList
        items={transformedData || []}
        loading={(!data && !PoSearcherror) || loading}
        hasNextPage={hasNextPage}
        showFooter={showFooter}
        columns={gridColumns.filter((column) => column.isVisible)}
        onRenderItemColumn={(item, index, column) =>
          renderItemColumn({
            item,
            index,
            column,
            styles,
            globalSearchText,
            transactionStampField,
            setTransactionStampField,
          })
        }
        onRenderFooterItemColumn={_renderDetailsFooterItemColumn}
        onRenderRow={_onRenderRow}
        onColumnHeaderClick={_onColumnClick}
        onLoadMore={async () => await handleSearch(true, false)}
        onSelectionChanged={setSelectedRows}
        requestedIndices={requestedIndices}
        totalRowsCount={totalCount1}
      />
      <StampTransactionFooter
        stampingAllowed={!!stampingAllowed}
        isStampTransactionMode={stampTransaction}
        invoices={transformedData}
        stampFields={transactionStampField}
        onStampProcessComplete={() => {
          expandedNav(true);
          setStampTransaction(false);
          setTransactionStampField(new Map([]));
          const newColumns = gridColumns.map((item) => {
            if (item.key === 'transactionStamp')
              return { ...item, isVisible: false };
            else return item;
          });
          setGridColumns(newColumns);
        }}
      />
    </>
  );
};
